import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for the pickup app's recommendation rails (API:
// /api/products/recommendations/*). Same convention as brandApi.js: reads return data or a safe
// default and toast on error; mutations return the result or null and let the caller toast
// success.
//
// Every call is per merchant. A merchant-type user is pinned server-side from its JWT and the
// merchantId sent here is ignored; a platform admin must name one.

const read = async (url, params, fallback, label) => {
  try {
    const res = await axiosInstance.get(url, { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error loading ${label}`);
    return fallback;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error loading ${label}`);
    return fallback;
  }
};

const mutate = async (method, url, payload, label) => {
  try {
    const res = await axiosInstance[method](url, payload);
    if (res?.data && !res.data.isError) return res.data.data ?? {};
    errorToast(res?.data?.message ?? `Error ${label}`);
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error ${label}`);
    return null;
  }
};

const BASE = "/api/products/recommendations";

// The curation pickers need ids and titles only. The shared getProductListing helper always
// asks for modifiers + brands, which multiplies every product by its modifiers on the server
// for a list that never shows either — on a large menu that single call was the heaviest thing
// this page did, and it re-ran after every save. Active products only, in pages of 1000;
// inactive products referenced by a rule come back with the rules themselves.
const PRODUCT_PAGE_SIZE = 1000;

export const getProductOptions = async (merchantId) => {
  const products = [];
  try {
    for (let pageNo = 1; ; pageNo += 1) {
      const res = await axiosInstance.get("/api/products/getAllProducts", {
        params: {
          merchantID: merchantId,
          pageNo,
          pageSize: PRODUCT_PAGE_SIZE,
          isActive: true,
          includeModifiers: false,
          includeBrands: false,
        },
      });
      if (!res?.data || res.data.isError) {
        errorToast(res?.data?.message ?? "Error loading products");
        break;
      }
      for (const p of res.data.data || []) {
        products.push({ id: p.id, title: p.title, arabicName: p.arabicName ?? null, is_active: p.is_active });
      }
      if (pageNo >= Number(res.data.totalPages || 1)) break;
    }
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error loading products");
  }
  return products;
};

export const getRecommendationRules = (merchantId, params = {}) =>
  read(`${BASE}/rules`, { merchantId, ...params }, { rules: [] }, "recommendation rules");

// Replaces the whole rule set for (merchant, slot, anchor), once per anchor.
//   anchors: null (every product) | [{ productId } | { appCategoryId }]
//   pins:    [{ productId | appCategoryId, sortOrder }]
//   blocked: [{ productId } | { appCategoryId }]
export const saveRecommendationRules = ({ merchantId, slot, anchors, pins, blocked }) =>
  mutate("put", `${BASE}/rules`, { merchantId, slot, anchors, pins, blocked }, "saving recommendation rules");

export const getRecommendationStats = (merchantId, anchorProductId) =>
  read(`${BASE}/stats`, { merchantId, anchorProductId: anchorProductId || undefined }, { freshness: null, pairs: [] }, "recommendation stats");

// Merchant-wide: every product with learned pairs, and the top sellers per time of day.
export const getRecommendationOverview = (merchantId) =>
  read(`${BASE}/overview`, { merchantId }, { windowDays: null, anchors: [], topSellers: {} }, "recommendation overview");

export const getRecommendationPreview = (merchantId, params) =>
  read(`${BASE}/preview`, { merchantId, ...params }, null, "preview");

export const getRecommendationPerformance = (merchantId, params) =>
  read(`${BASE}/performance`, { merchantId, ...params }, null, "recommendation performance");

export const getRecommendationConfig = (merchantId) =>
  read(`${BASE}/config`, { merchantId }, null, "recommendation settings");

// Only the keys sent change on the server; everything else is kept.
export const saveRecommendationConfig = (merchantId, changes) =>
  mutate("put", `${BASE}/config`, { merchantId, ...changes }, "saving recommendation settings");

export const recomputeRecommendations = (merchantId) =>
  mutate("post", `${BASE}/recompute`, { merchantId }, "starting the recompute");
